import getDefaultFilePickerListColumnInfo, {
    FilePickerListColumnInfo,
    getODCFilePickerListColumnInfo,
    getODBFilePickerListColumnInfo,
    getMailMessageListColumnInfo,
} from './filePickerListColumnInfo';
import {
    createFileProviderItemsSupportedActionsFunc,
    FileProviderItemsSupportedActions,
    mailboxProviderGetSupportedActions,
} from './fileProviderItemsSupportedActions';
import getColumnRenderFunctionFromAttachmentDataProvider, {
    ColumnRenderFunction,
} from './getColumnRenderFunction';
import FilePickerAction from '../../store/schema/FilePickerAction';
import FileProviderItemViewState from '../../store/schema/FileProviderItemViewState';
import { FilePickerViewType } from '../../types/FilePickerViewSwitcher';
import {
    AddableFileProviderInfo,
    getAddableFileProviderInfo as getAddableInfo,
} from 'owa-fileprovider-store';
import AttachmentDataProviderScope from 'owa-service/lib/contract/AttachmentDataProviderScope';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentItemsFilterBy from 'owa-service/lib/contract/AttachmentItemsFilterBy';
import { isFeatureEnabled } from 'owa-feature-flags';

interface FileProviderInfo {
    type: AttachmentDataProviderType;
    supportedFilePickerViews: FilePickerViewType[]; // Order of the view matters in the list as the first one would be the one shown to user by default
    addableFileProviderInfo: AddableFileProviderInfo | null | undefined; // Would be undefined if its being loaded otherwise if its not available then it would be null
    getColumnRenderFunction: (scope?: AttachmentDataProviderScope) => ColumnRenderFunction;
    getFilePickerListColumnInfo: (
        name: string,
        isSharedWithMe?: boolean
    ) => FilePickerListColumnInfo;
    showActionsPanel: boolean;
    filePickerFilterOptions: AttachmentItemsFilterBy[];
    getItemsSupportedActions: (
        items: FileProviderItemViewState[],
        isSharingFolderSupported: boolean,
        providerDisplayName: string
    ) => FileProviderItemsSupportedActions;
}
export default FileProviderInfo;

async function lazyInitializeFileProviderInfo(fileProviderInfo: FileProviderInfo): Promise<void> {
    fileProviderInfo.addableFileProviderInfo = getAddableInfo(fileProviderInfo.type);
}

function getDefaultFileProviderInfo(type: AttachmentDataProviderType): FileProviderInfo {
    const providerInfo: FileProviderInfo = {
        type: type,
        supportedFilePickerViews: [FilePickerViewType.List, FilePickerViewType.Tile],
        addableFileProviderInfo: undefined, // Would be lazily initialized
        getColumnRenderFunction: (scope?: AttachmentDataProviderScope) =>
            getColumnRenderFunctionFromAttachmentDataProvider(type, scope),
        getItemsSupportedActions: createFileProviderItemsSupportedActionsFunc({}),
        getFilePickerListColumnInfo: (name: string) => getDefaultFilePickerListColumnInfo(name),
        showActionsPanel: true,
        filePickerFilterOptions: [],
    };

    lazyInitializeFileProviderInfo(providerInfo);
    return providerInfo;
}

/**
 * The reason we lazy initialize the fileProviderInfoMap is that the strings might not be available when the module is executed as
 * they are lazy intiailized as well
 */
let fileProviderInfoMap: { [key: number]: FileProviderInfo } = null;
function getFileProviderInfoMap() {
    if (!fileProviderInfoMap) {
        fileProviderInfoMap = {};

        // Box
        const boxProviderInfo = getDefaultFileProviderInfo(AttachmentDataProviderType.Box);
        boxProviderInfo.supportedFilePickerViews = [FilePickerViewType.List];
        fileProviderInfoMap[AttachmentDataProviderType.Box] = boxProviderInfo;

        // Dropbox
        const dropboxProviderInfo = getDefaultFileProviderInfo(AttachmentDataProviderType.Dropbox);
        dropboxProviderInfo.supportedFilePickerViews = [FilePickerViewType.List];
        fileProviderInfoMap[AttachmentDataProviderType.Dropbox] = dropboxProviderInfo;

        // Facebook
        const facebookProviderInfo = getDefaultFileProviderInfo(
            AttachmentDataProviderType.Facebook
        );
        facebookProviderInfo.supportedFilePickerViews = [FilePickerViewType.Tile];
        facebookProviderInfo.showActionsPanel = false;
        facebookProviderInfo.getItemsSupportedActions = _ => ({
            actions: [FilePickerAction.Attach],
            messages: [],
        }); // We only allow attach action for Facebook, so do not show Actions Panel
        fileProviderInfoMap[AttachmentDataProviderType.Facebook] = facebookProviderInfo;

        // Google Drive
        const gDriveProviderInfo = getDefaultFileProviderInfo(AttachmentDataProviderType.GDrive);
        fileProviderInfoMap[AttachmentDataProviderType.GDrive] = gDriveProviderInfo;

        // Mailbox
        const mailboxProviderInfo = getDefaultFileProviderInfo(AttachmentDataProviderType.Mailbox);
        mailboxProviderInfo.getItemsSupportedActions = mailboxProviderGetSupportedActions;
        mailboxProviderInfo.showActionsPanel = false;
        mailboxProviderInfo.filePickerFilterOptions = [
            AttachmentItemsFilterBy.None,
            AttachmentItemsFilterBy.WordFiles,
            AttachmentItemsFilterBy.ExcelFiles,
            AttachmentItemsFilterBy.PowerPointFiles,
            AttachmentItemsFilterBy.PDFFiles,
        ];
        fileProviderInfoMap[AttachmentDataProviderType.Mailbox] = mailboxProviderInfo;

        // One Drive Consumer
        const oneDriveConsumerProviderInfo = getDefaultFileProviderInfo(
            AttachmentDataProviderType.OneDriveConsumer
        );
        oneDriveConsumerProviderInfo.getFilePickerListColumnInfo = (name: string) =>
            getODCFilePickerListColumnInfo(name);
        fileProviderInfoMap[
            AttachmentDataProviderType.OneDriveConsumer
        ] = oneDriveConsumerProviderInfo;

        // One Drive Pro
        const oneDriveProProviderInfo = getDefaultFileProviderInfo(
            AttachmentDataProviderType.OneDrivePro
        );
        fileProviderInfoMap[AttachmentDataProviderType.OneDrivePro] = oneDriveProProviderInfo;
        oneDriveProProviderInfo.getFilePickerListColumnInfo = (
            name: string,
            isSharedWithMe: boolean
        ) => getODBFilePickerListColumnInfo(name, isSharedWithMe);

        if (isFeatureEnabled('doc-fileProvider-wopiClient')) {
            const wopiBoxProviderInfo = getDefaultFileProviderInfo(
                AttachmentDataProviderType.WopiBox
            );
            wopiBoxProviderInfo.supportedFilePickerViews = [FilePickerViewType.List];
            fileProviderInfoMap[AttachmentDataProviderType.WopiBox] = wopiBoxProviderInfo;

            const wopiEgnyteProviderInfo = getDefaultFileProviderInfo(
                AttachmentDataProviderType.WopiEgnyte
            );
            wopiEgnyteProviderInfo.supportedFilePickerViews = [FilePickerViewType.List];
            fileProviderInfoMap[AttachmentDataProviderType.WopiEgnyte] = wopiEgnyteProviderInfo;

            const wopiDropboxProviderInfo = getDefaultFileProviderInfo(
                AttachmentDataProviderType.WopiDropbox
            );
            wopiDropboxProviderInfo.supportedFilePickerViews = [FilePickerViewType.List];
            fileProviderInfoMap[AttachmentDataProviderType.WopiDropbox] = wopiDropboxProviderInfo;
        }

        // Mail Messages
        const mailMessageProviderInfo = getDefaultFileProviderInfo(
            AttachmentDataProviderType.MailMessage
        );
        mailMessageProviderInfo.supportedFilePickerViews = [FilePickerViewType.List];
        mailMessageProviderInfo.getFilePickerListColumnInfo = (name: string) =>
            getMailMessageListColumnInfo(name);
        mailMessageProviderInfo.showActionsPanel = false;
        fileProviderInfoMap[AttachmentDataProviderType.MailMessage] = mailMessageProviderInfo;
    }

    return fileProviderInfoMap;
}

export function tryGetAddableFileProviderInfo(
    type: AttachmentDataProviderType
): AddableFileProviderInfo | null | undefined {
    if (type in getFileProviderInfoMap()) {
        return getFileProviderInfoMap()[type].addableFileProviderInfo;
    }

    throw new Error(`Unknown file provider type ${type.toString()}`);
}

/**
 * As we might want to get the AddableFileProviderInfo definitely as long as it exists.
 * This function helps in waiting for the AddableFileProviderInfo to load and then returns it.
 */
export function getAddableFileProviderInfo(
    type: AttachmentDataProviderType
): Promise<AddableFileProviderInfo | null> {
    /* tslint:disable:promise-must-complete */
    return new Promise<AddableFileProviderInfo>((resolve, reject) => {
        let tries = 0;
        const TIME_TO_WAIT_IN_MS = 1000;
        const MAX_TRIES = 5;

        const getInfoAndReturnOrRetry = () => {
            // 1- Try getting the AddableFileProviderInfo and if there is some error then just reject
            let addableFileProviderInfo = null;
            try {
                addableFileProviderInfo = tryGetAddableFileProviderInfo(type);
            } catch (error) {
                reject(error);
            }

            if (addableFileProviderInfo !== undefined) {
                // 2- If the AddableFileProviderInfo is available then resolve with it. Undefined means its not loaded but
                // null means that the AddableFileProviderInfo was explicitly set to null hence we also return that.
                resolve(addableFileProviderInfo);
            } else {
                tries += 1;

                if (tries <= MAX_TRIES) {
                    // 3- If AddableFileProviderInfo is not available and we can still
                    // try, then try getting it after a while
                    const timeToWaitForNextTryInMS = tries * TIME_TO_WAIT_IN_MS;
                    setTimeout(getInfoAndReturnOrRetry, timeToWaitForNextTryInMS);
                } else {
                    // 4- If we cannot retry any more then reject with error
                    reject(new Error(`Can not get the AddableFileProviderInfo for ${type}`));
                }
            }
        };

        // Start the execution
        getInfoAndReturnOrRetry();
    });
    /* tslint:enable:promise-must-complete */
}

export function getFileProviderInfo(type: AttachmentDataProviderType) {
    if (type in getFileProviderInfoMap()) {
        return getFileProviderInfoMap()[type];
    }

    throw new Error(`Unknown file provider type ${type.toString()}`);
}

export { FileProviderItemsSupportedActions };
